import { PAGE_CLASS_FOR_PRINTING } from 'components/Report/ReportPage/ReportPage';
import { getBrightness } from 'the-simplest-color-checker';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useMutation } from '@tanstack/react-query';

const formatToPageSize = {
  a4: {
    width: 210,
    height: 297,
  },
  letter: {
    width: 215.9,
    height: 279.4,
  },
} as const;

type Format = keyof typeof formatToPageSize;

const splitIntoPages = (
  imgData: string,
  pageBreaks: {
    position: number;
  }[]
) => pageBreaks.map(({ position }) => ({ imgData, position }));

const calculatePageBreaks = (section: HTMLElement | null, format: Format) => {
  if (!section) return [];

  const { width: sheetWidth, height: sheetHeight } = formatToPageSize[format];

  const originalWidth = section.offsetWidth;
  const originalHeight = section.scrollHeight || section.clientHeight; // Use both for better cross-browser support

  const pdfHeight = (sheetWidth / originalWidth) * originalHeight;

  let totalHeight = pdfHeight;
  let position = 0;

  const pageBreaks = [{ position }];
  totalHeight -= sheetHeight;

  while (totalHeight > 0) {
    position -= sheetHeight;
    pageBreaks.push({ position });
    totalHeight -= sheetHeight;
  }

  return pageBreaks;
};

const renderSectionToCanvas = async (section: HTMLElement) => {
  if (!section) {
    console.error('Section not found');
    return null;
  }

  const scale = 2;

  try {
    const canvas = await html2canvas(section, {
      scale,
      scrollX: 0,
      scrollY: 0,
      useCORS: true, // Enable cross-origin image rendering
      allowTaint: true, // Allow tainted images if needed
      ignoreElements: (el) => ['react-resizable-handle'].some((cls) => el.classList.contains(cls)),
      onclone: (clonedDoc) => {
        clonedDoc.body.style.padding = '12px';
        clonedDoc.body.style.whiteSpace = 'nowrap';
        clonedDoc.querySelectorAll('*').forEach((el) => {
          (el as HTMLElement).style.visibility = 'visible';
        });
        // colors need to be adjusted for better printing
        const typography: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.MuiTypography-root');
        const tableHead: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.MuiTableHead-root');
        const tableCell: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.MuiTableCell-root');
        const yTicks: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.y-tick');
        const xTicks: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.x-tick');
        const umpireConditionLabels: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll(
          '.UmpireConditions-config-container .MuiTypography-root'
        );
        const umpireStrikeZoneAccuracyLabels: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll(
          '.UmpireStrikeZoneAccuracyChart-metric-label'
        );
        const percentileRankingLabels: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.PercentileRankings .MuiTypography-root');
        const pitchTypeRangeLabels: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.PitchTypeRanges-container .MuiTypography-root');
        const coordinateSystemLabels: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.CoordinateSystem .MuiTypography-root');
        const pageHeaderTexts: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.ReportPageHeader-metadata .MuiTypography-root');
        const pageHeaders: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.ReportPageHeader-metadata');
        const legendItems: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.Legend-item-label');
        const strikeZoneHeatmapValues: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.StrikeZoneHeatmap .MuiTypography-root');
        const strikeZoneValues: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.StrikeZoneZones .value');
        const strikeZonePoints: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.StrikeZone-point > *');
        const counts: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll('.CountChart-grid-item-header');
        const pitchTypeUsageTotalCount: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll(
          '.PitcherSingleSessionPitchTypesUsageSummaryChart-total-value'
        );
        const singleBattingStatValues: NodeListOf<HTMLElement> = clonedDoc.querySelectorAll(
          '.BatterSingleSessionBattingStatChart-value-label'
        );
        const svgTexts: NodeListOf<SVGAElement> = clonedDoc.querySelectorAll('svg text');
        const tmLogoGrey: NodeListOf<SVGAElement> = clonedDoc.querySelectorAll('svg #tm-logo-svg .tm-logo-grey');
        [...typography, ...tableCell].forEach((text) => {
          text.style.fontSize = '18px';
          text.style.fill = 'black';
        });
        pageHeaderTexts.forEach((el, i) => {
          el.style.fontSize = i === 0 ? '22px' : '16px';
        });
        pageHeaders.forEach((el) => {
          el.style.gap = '16px';
        });
        tableHead.forEach((text) => {
          text.style.backgroundColor = '#f7f7f7';
        });
        [...yTicks, ...xTicks].forEach((el) => {
          el.style.backgroundColor = '#aeaeae';
        });
        [...umpireConditionLabels, ...percentileRankingLabels, ...pitchTypeRangeLabels].forEach((el) => {
          el.style.fontSize = '14px';
        });
        [
          ...umpireStrikeZoneAccuracyLabels,
          ...legendItems,
          ...strikeZoneHeatmapValues,
          ...strikeZoneValues,
          ...counts,
          ...coordinateSystemLabels,
          ...strikeZonePoints,
        ].forEach((el) => {
          el.style.fontSize = '12px';
        });
        singleBattingStatValues.forEach((el) => {
          el.style.fontSize = '40px';
        });
        pitchTypeUsageTotalCount.forEach((el) => {
          el.style.fontSize = '24px';
        });
        tmLogoGrey.forEach((el) => {
          el.style.fill = '#333333';
        });
        svgTexts.forEach((text: SVGAElement) => {
          const brightness = getBrightness(text.style.fill);
          text.style.fontFamily = 'Inter, sans-serif';
          if (parseInt(text.style.fontSize) < 30) text.style.fontSize = '30px';
          if (brightness < 160) text.style.fill = 'black';
        });
      },
    });

    return canvas;
  } catch (error) {
    console.error('Error capturing the report:', error);
    return null;
  }
};

const preparePdfFile = async (format: Format) => {
  const sections: HTMLElement[] = Array.from(document.querySelectorAll(`div.${PAGE_CLASS_FOR_PRINTING}`));
  if (!sections?.length) return;

  for (const section of sections) {
    if (!section) return;

    // Force layout to act like desktop if on mobile
    if (section.scrollWidth < 1080) {
      section.style.width = 1080 + 'px';
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for styles to apply
    }

    section.style.width = Math.ceil(section.scrollHeight * (formatToPageSize[format].width / formatToPageSize[format].height)) + 'px';
  }

  await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for styles to apply

  const tasks = sections.map(async (item) => {
    return await renderSectionToCanvas(item);
  });
  const results = await Promise.all(tasks);

  if (!results?.length) return;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format });

  const { width: sheetWidth } = formatToPageSize[format];

  results.forEach((canvas, resultIndex) => {
    if (!canvas) return;
    const imgData = canvas.toDataURL('image/png');

    const pageBreaks = calculatePageBreaks(sections[resultIndex], format);
    const pages = splitIntoPages(imgData, pageBreaks);

    const originalPagesCount = pdf.internal?.['getNumberOfPages']?.();

    pages.forEach(({ position }, index) => {
      if (resultIndex + index !== 0 && index + originalPagesCount > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, sheetWidth, (canvas.height * sheetWidth) / canvas.width, '', 'FAST');
    });
  });

  sections.forEach((section) => {
    section.style.width = 'auto';
  });

  await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for styles to apply

  return pdf;
};

const downloadReportAsPDF = async (pdfFileName: string, format: Format) => {
  await preparePdfFile(format).then((pdf) => {
    if (!pdf) return;
    pdf.save(pdfFileName);
  });
};

const printReport = async (format: Format) => {
  await preparePdfFile(format).then((pdf) => {
    if (!pdf) return;
    const pdfBlob = pdf.output('bloburl');
    const printWindow = window.open(pdfBlob, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        printWindow.onafterprint = () => printWindow.close();
      };
    }
  });
};

export const usePrintReport = () => {
  return useMutation({
    mutationFn: async ({ format }: { format: Format }) => {
      await printReport(format);
    },
  });
};

export const useDownloadReport = () => {
  return useMutation({
    mutationFn: async ({ fileName, format }: { fileName: string; format: Format }) => {
      await downloadReportAsPDF(fileName, format);
    },
  });
};
